import { Location } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService } from 'primeng/api';
import { fromEvent, merge, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { buildInfo } from '../../../environments/build-info.generated';
import { ConfirmDialogKeys } from '../../common/confirm-dialog-keys';
import { L10nStatic } from '../static/l10n.static';

interface RemoteVersionInfo {
	version?: string;
	buildId?: string;
}

interface ReloadPrompt {
	id: string;
	snoozeMs: number;
}

const VERSION_CHECK_INTERVAL_MS = 20_000;
const VERSION_CHECK_MIN_INTERVAL_MS = VERSION_CHECK_INTERVAL_MS;
const VERSION_CHECK_TIMEOUT_MS = 15_000;
const VERSION_PROMPT_SNOOZE_MS = 60 * 60_000;
const CHUNK_PROMPT_SNOOZE_MS = 30_000;
const VERSION_JSON_PATH = 'version.json';
const RELOAD_CACHE_BUSTER_PARAM = '_v';
const CHUNK_LOAD_FAILURE_ID = 'chunk-load-failure';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
	private readonly confirmationService = inject(ConfirmationService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly location = inject(Location);
	private readonly dismissedUntilById = new Map<string, number>();
	private updatePromptShown = false;
	private started = false;
	private lastVersionCheckAt = Number.NEGATIVE_INFINITY;
	private versionCheckInProgress = false;

	start(): void {
		if (this.started) {
			return;
		}

		this.started = true;
		this.clearReloadCacheBuster();

		merge(
			timer(0, VERSION_CHECK_INTERVAL_MS),
			fromEvent(document, 'visibilitychange')
				.pipe(filter(() => document.visibilityState === 'visible')),
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.scheduleVersionCheck());
	}

	reloadApp(): void {
		const url = new URL(window.location.href);
		url.searchParams.set(RELOAD_CACHE_BUSTER_PARAM, Date.now().toString());
		window.location.replace(url.toString());
	}

	notifyChunkLoadFailure(): void {
		this.promptForReload({
			id: CHUNK_LOAD_FAILURE_ID,
			snoozeMs: CHUNK_PROMPT_SNOOZE_MS,
		});
	}

	private scheduleVersionCheck(): void {
		const now = performance.now();
		if (
			this.versionCheckInProgress ||
			now - this.lastVersionCheckAt < VERSION_CHECK_MIN_INTERVAL_MS
		) {
			return;
		}

		this.lastVersionCheckAt = now;
		this.versionCheckInProgress = true;
		void this.checkVersion().finally(() => {
			this.versionCheckInProgress = false;
		});
	}

	private async checkVersion(): Promise<void> {
		const abortController = new AbortController();
		const timeoutId = window.setTimeout(
			() => abortController.abort(),
			VERSION_CHECK_TIMEOUT_MS,
		);

		try {
			const versionUrl = new URL(VERSION_JSON_PATH, document.baseURI);
			versionUrl.searchParams.set('_', Date.now().toString());
			const response = await fetch(versionUrl.toString(), {
				cache: 'no-store',
				signal: abortController.signal,
			});
			if (!response.ok) {
				return;
			}

			const remoteVersion = (await response.json()) as RemoteVersionInfo;
			const remoteBuildId = this.getRemoteBuildId(remoteVersion);
			const currentBuildId = remoteVersion.buildId ? buildInfo.buildId : buildInfo.version;
			if (remoteBuildId && remoteBuildId !== currentBuildId) {
				this.promptForReload({
					id: `build:${remoteBuildId}`,
					snoozeMs: VERSION_PROMPT_SNOOZE_MS,
				});
			}
		} catch {
			// Network issues should not interrupt the user session.
		} finally {
			window.clearTimeout(timeoutId);
		}
	}

	private getRemoteBuildId(remoteVersion: RemoteVersionInfo): string | null {
		if (typeof remoteVersion?.buildId === 'string' && remoteVersion.buildId) {
			return remoteVersion.buildId;
		}

		return typeof remoteVersion?.version === 'string' && remoteVersion.version ? remoteVersion.version : null;
	}

	private promptForReload({ id, snoozeMs }: ReloadPrompt): void {
		const now = performance.now();
		if (this.updatePromptShown || (this.dismissedUntilById.get(id) ?? 0) > now) {
			return;
		}

		this.dismissedUntilById.delete(id);
		this.updatePromptShown = true;
		this.confirmationService.confirm({
			key: ConfirmDialogKeys.appVersionUpdate,
			header: L10nStatic.translate('app.reload'),
			message: L10nStatic.translate('app.need_to_reload_page'),
			acceptLabel: L10nStatic.translate('app.reload'),
			rejectLabel: L10nStatic.translate('button.cancel'),
			rejectVisible: true,
			closable: true,
			closeOnEscape: true,
			accept: () => this.reloadApp(),
			reject: () => {
				this.updatePromptShown = false;
				this.dismissedUntilById.set(id, performance.now() + snoozeMs);
			},
		});
	}

	private clearReloadCacheBuster(): void {
		const url = new URL(window.location.href);
		if (!url.searchParams.has(RELOAD_CACHE_BUSTER_PARAM)) {
			return;
		}

		url.searchParams.delete(RELOAD_CACHE_BUSTER_PARAM);
		const appRelativePath = this.toAppRelativePath(url.pathname);
		this.location.replaceState(
			`${appRelativePath}${url.search}${url.hash}`,
			'',
			window.history.state,
		);
	}

	private toAppRelativePath(pathname: string): string {
		const basePath = new URL(document.baseURI).pathname;
		const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
		if (pathname.startsWith(normalizedBasePath)) {
			return `/${pathname.slice(normalizedBasePath.length)}`;
		}

		return pathname === normalizedBasePath.slice(0, -1) ? '/' : pathname;
	}
}
